/*
** $Id$
**
** Copyright 2018 Port25 Solutions, Inc.
** All rights reserved.
*/


import { pmta } from '../util/HttpsClient'


/**
 * Utility functions.
 */
export default class Utils {
    /**
     * Updates the given component.
     *
     * @param component
     *      what component to update.
     * @param url
     *      where to fetch new data for the component.
     * @param [callback]
     *      an optional callback to invoke when setState on the given
     *      component finishes.
     * @param urlParams
     *      what URL parameters to use.  If no parameters are desired, then
     *      this could be undefined.
     */
    async updateComponent(component, url, urlParams, callback = () => {}) {
        try {
            let result = await pmta.get(url, urlParams)
            component.setState(result, callback)
            component.props.showError()
        }
        catch (ex) {
            component.props.showError(ex.toString())
            component.csrfToken = ex.csrfToken
        }
    }


    /**
     * Sends a HTTP post request to the given URL and updates the specified
     * component with results.
     *
     * @param component
     *      what to update and from where to get the CSRF token.
     * @param url
     *      path relative to the web root. e.g., /enterLak for the LAK page.
     * @param parts
     *      JSON object containing name/value pairs of MIME parts to send.
     * @param refresh
     *      whether to refresh the component after a successful POST.
     * @param callback
     *      function to invoke after the POST request succeeds; this function is not
     *      invoked upon errors POSTing or updating the component.
     */
    async post(component, url, parts, refresh = true, callback = () => {}) {
        let successMessage
        let csrfToken = getFromJson(component.state, ['data', 'CSRFToken'])
            || component.csrfToken
        try {
            await pmta.post(url, Object.assign({
                CSRFToken: csrfToken,
                format: 'json'
            }, parts))
            refresh && await updateComponent(component, url)
            successMessage = 'Saved'
            component.props.showError('')
        }
        catch(ex) {
            component.props.showError(ex.toString())
        }
        component.setState({
            successMessage: successMessage
        }, () => {
            if (successMessage) {
                callback()
            }
        })
    }


    /**
     * Returns value from the given JSON object at the specified path.
     *
     * @param data
     *      JSON object to from where to get the value.
     * @param path
     *      an array of path components, each representing a key in a JSON
     *      object with one more level of nesting.
     * @param defaultValue
     *      what to return if the specified path doesn't exist.
     * @return
     *      the value at the specified path or <code>defaultValue</code>
     *      if the path doesn't exist.  In JavaScript, the following are
     *      considered as falsy values: false, 0, "", null, undefined, and NaN.
     *      This method falls back to returning the defaultValue only if
     *      the path resolves to undefined or null, and all other falsy values
     *      are returned as they are.
     */
    getFromJson(data, path, defaultValue) {
        if (typeof data !== 'object') {
            throw new Error('data must be an object')
        }
        if (!Array.isArray(path)) {
            throw new Error('path must be an array')
        }
        
        let reducer = (obj, key) => {
            return (obj === undefined || obj === null) ?  obj : obj[key]
        }
        let result = path.reduce(reducer, data)
        return (defaultValue !== undefined &&
            (result === undefined || result === null)) ? defaultValue : result
    }


    /**
     * Formats the given count as a percent of total.
     *
     * @param count
     *      count for which the percent is desired.
     * @param total
     *      total to compute the percent.
     * @return
     *      count as a percent of total.
     */
    formatPercent(count, total) {
        if (total === 0) {
            return "0%"
        }
        let pct = ((count / total) * 100)
        return (Math.round(pct * 100) / 100) + "%"
    }


    /**
     * Formats the given boolean value.
     *
     * @param what
     *      what to format, as received from PowerMTA.
     * @return
     *      formatted boolean value.
     */
    formatBoolean(what) {
        if (typeof what !== 'boolean') {
            return what
        }
        return what ? 'yes' : 'no'
    }
}


export let updateComponent = new Utils().updateComponent
export let post = new Utils().post
export let getFromJson = new Utils().getFromJson
export let formatPercent = new Utils().formatPercent
export let formatBoolean = new Utils().formatBoolean
